import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { api } from '../../services/api';

interface PackageDto {
  id: number;
  name: string;
  type: string;
  note?: string;
  selected: boolean;
}

interface PackageInfoResponse {
  success: boolean;
  data: {
    productId: string;
    packageInfo: Record<string, PackageDto[]>;
  };
}

interface PackageSelectorModalProps {
  show: boolean;
  productId: string | null;
  onHide: () => void;
  onSaved: (packageSummary: string) => void;
}

const PackageSelectorModal: React.FC<PackageSelectorModalProps> = ({ show, productId, onHide, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packagesByType, setPackagesByType] = useState<Record<string, PackageDto[]>>({});
  const [selectedByType, setSelectedByType] = useState<Record<string, Set<number>>>({});

  useEffect(() => {
    if (!show || !productId) return;

    const load = async () => {
      try {
        setLoading(true);
        const res = await api.getProductPackageInfo(productId);
        const data = res.data as PackageInfoResponse;

        const pkgInfo = data?.data?.packageInfo || {};
        setPackagesByType(pkgInfo);

        const initialSelected: Record<string, Set<number>> = {};
        Object.entries(pkgInfo).forEach(([type, list]) => {
          initialSelected[type] = new Set(
            list.filter(p => p.selected).map(p => p.id)
          );
        });
        setSelectedByType(initialSelected);
      } catch (error) {
        console.error('Failed to load package info:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [show, productId]);

  const togglePackage = (type: string, id: number) => {
    setSelectedByType(prev => {
      const next: Record<string, Set<number>> = { ...prev };
      const set = new Set(next[type] || []);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      next[type] = set;
      return next;
    });
  };

  const handleSave = async () => {
    if (!productId) return;

    try {
      setSaving(true);
      const payload: Record<string, number[]> = {};
      Object.entries(selectedByType).forEach(([type, set]) => {
        payload[type] = Array.from(set);
      });
      await api.updateProductPackageInfo(productId, payload);
      
      // Пересчитываем package_summary на основе выбранных упаковок
      const selectedNames: string[] = [];
      Object.entries(selectedByType).forEach(([type, set]) => {
        const packages = packagesByType[type] || [];
        packages.forEach(pkg => {
          if (set.has(pkg.id)) {
            selectedNames.push(pkg.name);
          }
        });
      });
      const packageSummary = selectedNames.length > 0 ? selectedNames.join(', ') : '';
      
      onSaved(packageSummary);
      onHide();
    } catch (error) {
      console.error('Failed to save package info:', error);
    } finally {
      setSaving(false);
    }
  };

  // Определенный порядок типов
  const typeOrder = ['base_type', 'branding_type', 'supply_note', 'additional_materials'];
  
  const sortedTypes = useMemo(() => {
    const types = Object.keys(packagesByType);
    return types.sort((a, b) => {
      const indexA = typeOrder.indexOf(a);
      const indexB = typeOrder.indexOf(b);
      // Если тип есть в порядке - сортируем по порядку, иначе в конец
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [packagesByType]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Выбор упаковки</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" />
          </div>
        ) : sortedTypes.length === 0 ? (
          <div>Нет доступных упаковок.</div>
        ) : (
          <Row className="g-3">
            {sortedTypes.map((type) => (
              <Col key={type} xs={12} md={6}>
                <div className="package-type-section p-3 border rounded bg-dark h-100">
                  <div className="package-type-header mb-2">
                    Тип упаковки: {type}
                  </div>
                  <div className="d-flex flex-column gap-1">
                    {packagesByType[type].map((pkg) => {
                      const hasNote = pkg.note && pkg.note.trim().length > 0;
                      return (
                        <div key={pkg.id} className="package-option mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`pkg-${type}-${pkg.id}`}
                            label={pkg.name}
                            checked={selectedByType[type]?.has(pkg.id) || false}
                            onChange={() => togglePackage(type, pkg.id)}
                            className="package-checkbox"
                          />
                          {hasNote && (
                            <div className="package-note text-muted small">
                              {pkg.note}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Отмена
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PackageSelectorModal;


